// components/FacilityPickerDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Spinner,
  useToast,
  Box,
  Flex,
  Tag,
  TagLabel,
  Divider,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useCognito } from "../utils/useCognito";
import { getBaaDataForUser } from "../utils/commonApiCalls";

const normalize = (s) => (s || "").toLowerCase();

export default function FacilityPickerDrawer({
  isOpen,
  onClose,
  userId,
  onSelectFacility,
}) {
  const { getIdToken } = useCognito();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    if (!userId) {
      setFacilities([]);
      return;
    }

    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        const token = getIdToken();
        const baa = await getBaaDataForUser(userId, token);

        const list = [];
        if (Array.isArray(baa?.facilities)) {
          for (const f of baa.facilities) {
            list.push({
              id: f.id || f.facilityId || f.FacilityId || "",
              name: f.name || f.facilityName || "Unnamed Facility",
              npi: f.npi || f.NPI || f.facilityNPI || "",
              tin: f.tin || f.TIN || f.facilityTIN || "",
              address: [
                f.street || f.address,
                f.city,
                f.state,
                f.zip,
              ]
                .filter(Boolean)
                .join(", "),
            });
          }
        } else if (baa) {
          const id = baa.facilityId || baa.facilityAddress?.id || "";
          const name =
            baa.facility?.name ||
            baa.facilityAddress?.name ||
            "Unnamed Facility";
          const npi =
            baa.facility?.npi || baa.facilityAddress?.npi || "";
          const tin =
            baa.facility?.tin || baa.facilityAddress?.tin || "";
          const addr = [
            baa.facility?.street || baa.facilityAddress?.street,
            baa.facility?.city || baa.facilityAddress?.city,
            baa.facility?.state || baa.facilityAddress?.state,
            baa.facility?.zip || baa.facilityAddress?.zip,
          ]
            .filter(Boolean)
            .join(", ");
          list.push({ id, name, npi, tin, address: addr });
        }

        if (mounted) setFacilities(list);
      } catch (err) {
        console.error("Facility fetch failed:", err);
        toast({
          title: "Failed to load facilities",
          description: err.message || "Check BAA data for this user.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOpen, userId, getIdToken, toast]);

  const filtered = useMemo(() => {
    if (!search) return facilities;
    const q = normalize(search);
    return facilities.filter((f) =>
      [f.name, f.npi, f.tin, f.address]
        .filter(Boolean)
        .some((v) => normalize(v).includes(q))
    );
  }, [facilities, search]);

  const renderBodyContent = () => {
    if (!userId) {
      return (
        <Flex flex="1" align="center" justify="center">
          <Text color="gray.500" textAlign="center">
            Pick a user in the invoice form first,
            <br />
            then you can select one of their facilities here.
          </Text>
        </Flex>
      );
    }

    if (loading) {
      return (
        <Flex flex="1" align="center" justify="center">
          <Spinner size="lg" />
        </Flex>
      );
    }

    return (
      <Box
        flex="1"
        minH={0}
        maxH="calc(100vh - 220px)"
        overflowY="auto"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.700"
        p={2}
      >
        {filtered.length === 0 ? (
          <Flex align="center" justify="center" h="100%">
            <Text color="gray.500">
              No facilities found for this user.
            </Text>
          </Flex>
        ) : (
          <VStack align="stretch" spacing={2}>
            {filtered.map((f, idx) => (
              <Box
                key={`${f.id || "noid"}-${idx}`}
                borderRadius="lg"
                borderWidth="1px"
                borderColor="gray.700"
                p={3}
                cursor="pointer"
                _hover={{ bg: "teal.50", borderColor: "teal.400" }}
                transition="background-color 0.12s ease-out, border-color 0.12s ease-out"
                onClick={() => {
                  onSelectFacility?.(f);
                  onClose?.();
                }}
              >
                <VStack align="flex-start" spacing={0}>
                  <HStack justify="space-between" w="100%">
                    <Text fontWeight="semibold">{f.name}</Text>
                    {(f.npi || f.tin) && (
                      <HStack spacing={2}>
                        {f.npi && (
                          <Tag
                            size="sm"
                            variant="subtle"
                            colorScheme="purple"
                          >
                            <TagLabel>NPI: {f.npi}</TagLabel>
                          </Tag>
                        )}
                        {f.tin && (
                          <Tag
                            size="sm"
                            variant="subtle"
                            colorScheme="gray"
                          >
                            <TagLabel>TIN: {f.tin}</TagLabel>
                          </Tag>
                        )}
                      </HStack>
                    )}
                  </HStack>
                  {f.address && (
                    <Text fontSize="sm" color="gray.500">
                      {f.address}
                    </Text>
                  )}
                </VStack>
                <Divider my={2} opacity={0.2} />
                <Text fontSize="xs" color="gray.500">
                  Click to use this facility
                  {f.id ? ` (ID: ${f.id})` : ""}.
                </Text>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    );
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">Select Facility</DrawerHeader>
        <DrawerBody pt={4} display="flex" flexDirection="column" gap={4}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, NPI, TIN or address"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              borderRadius="xl"
            />
          </InputGroup>

          {renderBodyContent()}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
